import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { queryClient, QueryKey } from '@/config/query-client';
import { notify } from '@/ui/snackbar/notify';
import { companyIdState } from '../company.state';
import { useCompanyFetchMode } from '../company-fetch-context';
import type { CreateCompanyContactBodyPayload } from '@/api/v4/organization-contacts.api';
import { createCompanyContacts } from '@/api/v4/organization-contacts.api';
import { useAtomValue } from 'jotai';

export const useCreateCompanyContacts = () => {
  const { t } = useTranslation('default');
  const companyId = useAtomValue(companyIdState);
  const fetchMode = useCompanyFetchMode();

  const mutation = useMutation({
    mutationFn: createCompanyContacts,
    onError: () => {
      notify({
        message: t`unknownError`,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Company, fetchMode, companyId],
        }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.LastActivities] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.LastActivitiesCount],
        }),
      ]);
    },
  });

  const createContacts = (data: CreateCompanyContactBodyPayload) =>
    companyId
      ? mutation.mutateAsync({
          companyId,
          data,
        })
      : undefined;

  return {
    createContacts,
    isError: mutation.isError,
  };
};
